import {
  CheckboxField,
  DropdownSubmitButton,
  Form,
  NumericFormatField,
  SelectField,
  SubmitButton,
  TextInputField,
} from '@/components';
import { GovernmentSearchField } from '@/features/government-search/_components/GovernmentSearchField';
import { getCodeMaxValue } from '@/features/master-table/utils/get-code-max-value';
import { validateInputPayment } from '@/features/project/_utils/payment-form-control';
import { PaymentFormSchema } from '@/features/project/projects/_models/payment';
import { usePaymentGroupOptions } from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useForm, useId } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof PaymentFormSchema>;
type CreatePaymentNextDrawerKey =
  | 'payment-contract'
  | 'payee-payment'
  | 'contractor-payment';

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: ({
    values,
    nextDrawerKey,
    onSuccess,
  }: {
    values: FormValues;
    nextDrawerKey?: CreatePaymentNextDrawerKey;
    onSuccess?: () => void;
  }) => Promise<void>;
  isEditing?: boolean;
};

export const PaymentForm: FC<Props> = ({
  initialValues,
  onSubmit,
  isEditing = false,
}) => {
  const { t } = useTranslation();

  const { id } = useId();
  const formMethods = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(PaymentFormSchema),
  });
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    clearErrors,
    setError,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = formMethods;

  const paymentGroupOptions = usePaymentGroupOptions(id);
  const [isOthers] = watch(['isOthers', 'name']);

  const saveAndCreateNextItem =
    initialValues?.type === 'payee' ? t('payment') : t('contractor');

  return (
    <FormProvider {...formMethods}>
      <Form
        leavePageAlertCondition={{
          isDirty,
          isSubmitting,
          isSubmitSuccessful,
        }}
        layout="vertical"
        onFinish={handleSubmit((values) => {
          const { hasError } = validateInputPayment(values, setError);
          if (hasError) {
            return;
          }
          onSubmit({ values });
        })}
      >
        <SelectField
          name="paymentGroupId"
          label={t('payment-group')}
          control={control}
          options={paymentGroupOptions.data}
          errors={errors}
          disabled={!!initialValues?.paymentGroupId}
          required
        />

        <GovernmentSearchField
          name="name"
          label={
            initialValues?.type === 'contractor'
              ? t('contractor-name')
              : t('payment-name')
          }
          companyNameHint={
            initialValues?.type === 'payee'
              ? t('hint-payment-name')
              : t('hint-contractor-name')
          }
          defaultSearchValue={initialValues?.name}
          companyNumberHint={t('hint-payment-by-company-number')}
          listHeight={512}
          control={control}
          errors={errors}
          onSelect={({ corporateInfo }) => {
            if (corporateInfo) {
              setValue(
                'corporateNumber',
                Number(corporateInfo.corporateNumber),
              );
              setValue('name', corporateInfo.corporateName);
              setValue('corporateAddress', corporateInfo.corporateAddress);
              setValue('corporateKind', corporateInfo.corporateKind);
            }
          }}
          onChangeSearchType={() => {
            setValue('corporateNumber', null);
            setValue('corporateAddress', null);
            setValue('corporateKind', null);
          }}
          onChange={(value) => {
            setValue('name', value, { shouldDirty: true });
          }}
          required
        />

        {initialValues?.type !== 'contractor' && (
          <CheckboxField
            name="isOthers"
            text={t('is-other-payment')}
            hint={t('hint-is-other-payment')}
            shouldCoexistOnChange
            onChange={() => {
              clearErrors('corporateNumber');
            }}
            control={control}
            errors={errors}
          />
        )}

        <NumericFormatField
          name="corporateNumber"
          label={t('corporate-number')}
          hint={
            initialValues?.type === 'contractor'
              ? t('hint-contractor-corporate-number')
              : t('hint-corporate-number')
          }
          control={control}
          errors={errors}
          disabled={isOthers}
          max={getCodeMaxValue(13)}
          min={0}
          required={!isOthers}
          disableSeparator={true}
        />

        <TextInputField
          name="corporateAddress"
          label={t('corporate-address')}
          control={control}
          errors={errors}
          disabled
        />

        <TextInputField
          name="corporateKind"
          label={t('corporate-kind')}
          control={control}
          errors={errors}
          maxLength={3}
          disabled
        />

        {isEditing ? (
          <SubmitButton loading={isSubmitting} disabled={!isDirty} />
        ) : (
          <DropdownSubmitButton<CreatePaymentNextDrawerKey>
            loading={isSubmitting}
            menuItems={[
              {
                key: 'payment-contract',
                label: t('save-and-create-item', {
                  item: t('payment-contract'),
                }),
              },
              {
                key:
                  initialValues?.type === 'payee'
                    ? 'payee-payment'
                    : 'contractor-payment',
                label: t('save-and-create-next-item', {
                  item: saveAndCreateNextItem,
                }),
              },
            ]}
            onClick={(e) => {
              handleSubmit(async (values) => {
                await onSubmit({
                  values,
                  nextDrawerKey: e.key,
                  onSuccess: () => {
                    if (
                      e.key === 'payee-payment' ||
                      e.key === 'contractor-payment'
                    ) {
                      reset(initialValues);
                    }
                  },
                });
              })();
            }}
          />
        )}
      </Form>
    </FormProvider>
  );
};
